import { useEffect, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { api, type BedarfMatrix, type BedarfMatrixAchse, type WerkstoffGruppe } from '../lib/api';
import { FormatDonut, StapelBalken, WEITERE, formatFarbe, type FormatInfo, type StapelZeile } from './FormatChart';

// Auswertung über der Blechbedarf-Liste (auch Ziel von „Top-Werkstoffe“ im Einkauf-Dashboard, mit gesetztem Werkstoff-Filter):
// welche Blechstärke (mm) in welchem Format wie oft bestellt wurde — samt Preis, Trend, letztem Kauf, Hauptlieferant.
//  1) gestapelte Balken je Blechstärke, Segmente = Formate
//  2) Detail zur gewählten Blechstärke: Donut + Formate DIESER Blechstärke
//  3) häufigste Kombinationen (Blechstärke · Format)
// Die Auswertung folgt den Listen-Filtern (Status, Suche, Werkstoff, Lieferant, Art) — nur Dicke/Format nicht,
// damit die Übersicht beim Anklicken stehen bleibt. Klicks setzen den Dicke-/Format-Filter der Liste.

const KEY = 'blechbedarf-diagramme-offen';
const lesen = (): boolean => { try { return localStorage.getItem(KEY) !== '0'; } catch { return true; } };
const schreiben = (v: boolean) => { try { localStorage.setItem(KEY, v ? '1' : '0'); } catch { /* ohne Speicher weiter */ } };

export type BedarfDiagrammFilter = { status?: string; q?: string; kat?: string; typ?: string; werkstoff?: string; lieferant?: string; ohne_angefragt?: boolean };

const pipe = (a: BedarfMatrixAchse | undefined) => (a ? a.raw.join('|') : '');
// „gewählt“ nur bei gesetztem Filter (leerer Filter darf nicht mit der leeren Schreibweise „—“ übereinstimmen)
const ist = (cur: string, a: BedarfMatrixAchse | undefined) => cur !== '' && cur === pipe(a);
const mm = (label: string, einheit: string) => (label === '—' ? label : `${label} ${einheit}`);
const eur = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2).replace('.', ',')} €`);
const datum = (v?: string | null): string => {
  const m = v?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (v ?? '—');
};
const preisText = (p: number | null, e: string | null) => (p == null ? '—' : `${p.toLocaleString('de-DE')} €${e ? `/${e}` : ''}`);

function Trend({ pct, titel }: { pct: number | null; titel?: string }) {
  if (pct == null) return <span className="text-slate-300" title="kein Vorjahresvergleich möglich">–</span>;
  const cls = pct > 2 ? 'text-red-600' : pct < -2 ? 'text-emerald-600' : 'text-slate-500';
  return <span className={`font-semibold tabular-nums ${cls}`} title={titel}>{pct > 2 ? '↑' : pct < -2 ? '↓' : '→'} {pct > 0 ? '+' : ''}{pct.toFixed(0)} %</span>;
}

function Kachel({ label, wert, sub, testid }: { label: string; wert: string; sub?: React.ReactNode; testid: string }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-4 py-3" data-testid={testid}>
      <div className="text-[11px] text-slate-500 uppercase tracking-wide leading-tight">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-0.5 tabular-nums truncate">{wert}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export function BedarfDiagramme({ filter, dicke, format, onWahl, dickeName = 'Blechstärke', formatName = 'Format', einheit = 'mm', werkstoff }: {
  filter: BedarfDiagrammFilter; dicke: string; format: string;
  onWahl: (dicke: string | null, format: string | null) => void;
  dickeName?: string; formatName?: string; einheit?: string; werkstoff?: string;
}) {
  const [offen, setOffen] = useState(lesen);
  const m = useApi<BedarfMatrix>(() => api.blechbedarf.matrix(filter), [JSON.stringify(filter)], offen);
  const d = m.data;
  const umschalten = () => setOffen(o => { schreiben(!o); return !o; });

  const gesamt = d?.gesamt ?? 0;
  const anteil = (n: number) => (gesamt ? Math.round((n / gesamt) * 100) : 0);
  const formate: FormatInfo[] = (d?.formate ?? []).map(f => ({ key: f.key, label: f.label, n: f.n }));
  const dicken = d?.dicken ?? [];
  const segsJe = new Map<string, { fk: string; n: number }[]>();
  for (const z of d?.zellen ?? []) (segsJe.get(z.dk) ?? segsJe.set(z.dk, []).get(z.dk)!).push({ fk: z.fk, n: z.n });
  const zeilen: StapelZeile[] = dicken.map(z => ({ key: z.key, label: z.label, n: z.n, segs: segsJe.get(z.key) ?? [] }));

  const zeileGew = dicken.find(z => ist(dicke, z))?.key ?? null;
  const formatGew = (d?.formate ?? []).find(f => ist(format, f))?.key ?? null;
  const dLabel = dicken.find(z => ist(dicke, z))?.label;
  const fLabel = (d?.formate ?? []).find(f => ist(format, f))?.label;
  const aktiv = dicke !== '' || format !== '';

  // Detail: gewählte Blechstärke, sonst die häufigste
  const fokus = dicken.find(z => z.key === zeileGew) ?? [...dicken].sort((x, y) => y.n - x.n)[0];
  const fokusDetail = d?.detail?.find(g => g.key === fokus?.key);
  const fokusSegs = (segsJe.get(fokus?.key ?? '') ?? []).filter(s => s.n > 0).sort((a, b) => b.n - a.n);
  const fokusFormate = fokusSegs.map(s => {
    const info = formate.find(f => f.key === s.fk);
    const ach = (d?.formate ?? []).find(f => f.key === s.fk);
    return { fk: s.fk, label: info?.label ?? '—', n: s.n, ach, farbe: formatFarbe(formate, s.fk).bg, stats: fokusDetail?.formate.find(g => g.key === s.fk) as WerkstoffGruppe | undefined };
  });
  const donutSegs = (() => {
    const eigene = fokusFormate.filter(f => f.farbe !== WEITERE).map(f => ({ key: f.fk, label: f.label, n: f.n, farbe: f.farbe }));
    const rest = fokusFormate.filter(f => f.farbe === WEITERE).reduce((s, f) => s + f.n, 0);
    return rest > 0 ? [...eigene, { key: '__weitere', label: 'Weitere', n: rest, farbe: WEITERE }] : eigene;
  })();
  const maxFokus = Math.max(1, ...fokusFormate.map(f => f.n));
  // Hamdi 14.10.2026: Karte darf nicht endlos nach unten wachsen → Top 5 + „Weitere“ (aufklappbar).
  // Ein gewähltes Format bleibt immer sichtbar, auch wenn es nicht unter den Top 5 ist.
  const [alleFormate, setAlleFormate] = useState(false);
  useEffect(() => { setAlleFormate(false); }, [fokus?.key]);
  const TOP = 5;
  const fokusSichtbar = alleFormate ? fokusFormate
    : fokusFormate.filter((f, i) => i < TOP || (zeileGew !== null && ist(format, f.ach)));
  const fokusRest = fokusFormate.filter(f => !fokusSichtbar.includes(f));
  const fokusRestN = fokusRest.reduce((s2, f) => s2 + f.n, 0);
  const k = d?.kpi;
  const titel = [werkstoff, `${dickeName} × ${formatName}`].filter(Boolean).join(' · ');

  const zeileKlick = (key: string) => {
    const z = dicken.find(x => x.key === key);
    onWahl(z && ist(dicke, z) ? null : pipe(z), null);
  };
  const segKlick = (zk: string, fk: string) => {
    const z = dicken.find(x => x.key === zk), f = (d?.formate ?? []).find(x => x.key === fk);
    if (z && f && ist(dicke, z) && ist(format, f)) onWahl(null, null); else onWahl(pipe(z), pipe(f));
  };
  const formatKlick = (fk: string) => {
    const f = (d?.formate ?? []).find(x => x.key === fk);
    onWahl(null, f && ist(format, f) ? null : pipe(f));
  };

  return (
    <section className="mb-4" data-testid="bedarf-diagramme">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <button type="button" onClick={umschalten} aria-expanded={offen} data-testid="diagramme-toggle" className="font-semibold text-slate-900 text-left">
          📊 Auswertung · {titel}
          {d && <span className="ml-2 font-normal text-sm text-slate-500" data-testid="diagramme-gesamt">{gesamt} Positionen im aktuellen Filter</span>}
          <span className="ml-3 text-sm font-normal text-slate-500">{offen ? 'ausblenden ▴' : 'einblenden ▾'}</span>
        </button>
        {offen && aktiv && (
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Filter:</span>
            {dicke !== '' && <button type="button" data-testid="chip-dicke" onClick={() => onWahl(null, format || null)} className="bg-sky-100 text-sky-800 rounded-full px-2.5 py-0.5">{dLabel ? mm(dLabel, einheit) : dicke} ✕</button>}
            {format !== '' && <button type="button" data-testid="chip-format" onClick={() => onWahl(dicke || null, null)} className="bg-sky-100 text-sky-800 rounded-full px-2.5 py-0.5">{fLabel ?? format} ✕</button>}
            <button type="button" data-testid="diagramme-reset" onClick={() => onWahl(null, null)} className="text-brand underline">Auswahl aufheben</button>
          </div>
        )}
      </div>

      {offen && (
        <div>
          {m.loading && !d && <div className="text-sm text-slate-500 py-4">Lädt …</div>}
          {m.error && <div className="text-sm text-red-600 py-2">Fehler: {m.error.message}</div>}
          {d && gesamt === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-5 text-sm text-slate-500">Keine Positionen im aktuellen Filter.</div>}
          {d && gesamt > 0 && (
            <>
              {k && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="kpi">
                  <Kachel testid="kpi-kaeufe" label="Positionen gesamt" wert={String(k.n_gesamt)} sub={`${k.n_12m} in den letzten 12 Monaten`} />
                  <Kachel testid="kpi-preis" label="Ø €/kg · 12 Monate" wert={k.avg_kg_12m != null ? eur(k.avg_kg_12m) : '—'}
                          sub={k.avg_kg_vor != null ? <><Trend pct={k.trend_pct} /> <span>gg. Vorjahr ({eur(k.avg_kg_vor)})</span></> : 'kein Vorjahresvergleich'} />
                  <Kachel testid="kpi-letzter" label="Letzter Kauf" wert={datum(k.letzter?.datum)}
                          sub={k.letzter ? `${preisText(k.letzter.preis, k.letzter.einheit)}${k.letzter.lieferant ? ` · ${k.letzter.lieferant}` : ''}` : undefined} />
                  <Kachel testid="kpi-lieferant" label="Meist bei" wert={k.haupt_lieferant ?? '—'} sub={k.haupt_lieferant ? `${k.haupt_n} Positionen` : undefined} />
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1.1fr)_minmax(0,0.9fr)] gap-4">
                {/* 1) gestapelte Balken */}
                <StapelBalken titel={`Nach ${dickeName} — aufgeteilt nach ${formatName}`} sub="Segment oder Zeile anklicken filtert die Liste"
                              zeilen={zeilen} formate={formate} gesamt={gesamt} zeileGew={zeileGew} formatGew={formatGew}
                              onZeile={zeileKlick} onSeg={segKlick} onFormat={formatKlick} einheit={einheit} zeilenName={dickeName} />

                {/* 2) Detail zur gewählten Blechstärke */}
                {fokus && (
                  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="detail">
                    <h2 className="font-semibold text-slate-900 text-[15px]">
                      {formatName} bei <span data-testid="detail-titel">{mm(fokus.label, einheit)}</span>
                    </h2>
                    <div className="text-xs text-slate-500 mb-3">
                      {fokus.n} Positionen{zeileGew === null ? ' — häufigste Blechstärke; links eine Zeile anklicken zum Wechseln' : ''}
                    </div>
                    <div className="flex items-center gap-5 flex-wrap mb-4">
                      <FormatDonut segmente={donutSegs} mitte={fokus.n} mitteText="Positionen" />
                      {fokusDetail && (
                        <div className="text-[13px] space-y-1.5 min-w-0" data-testid="detail-kennzahlen">
                          <div><span className="text-slate-500">Ø €/kg 12 M</span> <b className="text-slate-900 tabular-nums">{eur(fokusDetail.avg_kg_12m ?? fokusDetail.avg_kg)}</b> <Trend pct={fokusDetail.trend_pct} /></div>
                          <div><span className="text-slate-500">Spanne</span> <span className="tabular-nums text-slate-800">{fokusDetail.min_kg != null ? `${eur(fokusDetail.min_kg)} – ${eur(fokusDetail.max_kg)}` : 'keine kg-Preise'}</span></div>
                          <div><span className="text-slate-500">Letzter Kauf</span> <span className="text-slate-800">{datum(fokusDetail.letzter.datum)}</span></div>
                          {fokusDetail.haupt_lieferant && <div><span className="text-slate-500">Meist bei</span> <b className="text-slate-900">{fokusDetail.haupt_lieferant}</b> <span className="text-slate-400">({fokusDetail.haupt_n}×)</span></div>}
                        </div>
                      )}
                    </div>
                    <div className="space-y-1">
                      {fokusSichtbar.map(f => {
                        const gew = zeileGew !== null && ist(format, f.ach);
                        const leer = f.label === '—' || fokus.label === '—';
                        return (
                          <button key={f.fk || '—'} type="button" disabled={leer} data-testid={`detail-format-${f.fk || 'leer'}`} aria-pressed={gew}
                                  onClick={() => gew ? onWahl(null, null) : onWahl(pipe(fokus), pipe(f.ach))}
                                  className={`w-full text-left rounded-lg px-2 py-1 border ${gew ? 'border-sky-400 bg-sky-50' : 'border-transparent hover:bg-slate-50'} disabled:hover:bg-transparent`}>
                            <div className="flex items-center gap-2 text-[13px]">
                              <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: f.farbe }} />
                              <span className="w-20 shrink-0 font-semibold text-slate-800 truncate" title={f.label}>{f.label}</span>
                              <span className="flex-1 h-3.5 relative"><span className="absolute inset-y-0 left-0 rounded-r" style={{ width: `${(f.n / maxFokus) * 100}%`, background: f.farbe, minWidth: 3 }} /></span>
                              <span className="tabular-nums text-slate-900 w-8 text-right font-semibold">{f.n}</span>
                              <span className="tabular-nums text-[11px] text-slate-400 w-9">{Math.round((f.n / fokus.n) * 100)} %</span>
                            </div>
                            {f.stats && (
                              <div className="pl-[1.5rem] text-[11px] text-slate-500 flex flex-wrap items-center gap-x-3">
                                <span>Ø <b className="text-slate-700 tabular-nums">{eur(f.stats.avg_kg_12m ?? f.stats.avg_kg)}</b>/kg</span>
                                <Trend pct={f.stats.trend_pct} />
                                <span>letzter Kauf {datum(f.stats.letzter.datum)}</span>
                                {f.stats.haupt_lieferant && <span>bei <b className="text-slate-700">{f.stats.haupt_lieferant}</b></span>}
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {fokusRest.length > 0 && (
                        <button type="button" data-testid="detail-weitere" onClick={() => setAlleFormate(true)}
                                className="w-full text-left rounded-lg px-2 py-1.5 text-[13px] text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                          <span className="inline-block w-3 h-3 rounded-full shrink-0" style={{ background: WEITERE }} />
                          <span className="flex-1">Weitere {fokusRest.length} {formatName === 'Format' ? 'Formate' : formatName} <span className="text-slate-400">({fokusRestN} Pos.)</span></span>
                          <span className="text-brand text-xs font-medium">anzeigen ▾</span>
                        </button>
                      )}
                      {alleFormate && fokusFormate.length > TOP && (
                        <button type="button" data-testid="detail-weniger" onClick={() => setAlleFormate(false)}
                                className="w-full text-left rounded-lg px-2 py-1 text-xs text-brand hover:bg-slate-50">nur Top {TOP} zeigen ▴</button>
                      )}
                    </div>
                  </div>
                )}

                {/* 3) häufigste Kombinationen */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5" data-testid="kombis">
                  <h2 className="font-semibold text-slate-900 text-[15px]">Am häufigsten bestellt</h2>
                  <div className="text-xs text-slate-500 mb-3">Top {d.kombis.length} Kombinationen — anklicken filtert die Liste</div>
                  <div className="space-y-1">
                    {d.kombis.map((kb, i) => {
                      const rd = kb.raw_dicke.join('|'), rf = kb.raw_format.join('|');
                      const gew = dicke !== '' && format !== '' && dicke === rd && format === rf;
                      const leer = kb.dicke === '—' || kb.format === '—';
                      const farbe = formatFarbe(formate, kb.fk).bg;
                      const maxK = Math.max(1, ...d.kombis.map(x => x.n));
                      return (
                        <button key={`${kb.dk}|${kb.fk}`} type="button" disabled={leer} data-testid={`kombi-${i}`} aria-pressed={gew}
                                onClick={() => gew ? onWahl(null, null) : onWahl(rd, rf)}
                                title={`${mm(kb.dicke, einheit)} · ${kb.format}: ${kb.n} Positionen (${anteil(kb.n)} %)`}
                                className={`w-full text-left rounded-lg px-2 py-1 border ${gew ? 'border-sky-400 bg-sky-50' : 'border-transparent hover:bg-slate-50'} disabled:hover:bg-transparent`}>
                          <div className="flex items-center gap-2 text-[13px]">
                            <span className="w-24 shrink-0 font-semibold text-slate-800 truncate">{mm(kb.dicke, einheit)} · {kb.format}</span>
                            <span className="flex-1 min-w-0 h-4 relative"><span className="absolute inset-y-0 left-0 rounded-r-md" style={{ width: `${(kb.n / maxK) * 100}%`, background: farbe, minWidth: 3 }} /></span>
                            <span className="tabular-nums text-slate-900 w-8 shrink-0 text-right font-semibold">{kb.n}</span>
                            <span className="tabular-nums text-[11px] text-slate-400 w-8 shrink-0">{anteil(kb.n)} %</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
}
